import { makeStyles, tokens, Text, Spinner } from '@coreai/fluentui-react';
import { RunItem } from './RunItem';
import type { Run } from '@/types/monitoring';

type RunsListProps = {
  runs: Run[];
  isLoading: boolean;
  selectedRunId: string | null;
  selectedExecutionId: string | null;
  expandedRunIds: Set<string>;
  onSelectRun: (runId: string) => void;
  onSelectExecution: (executionId: string) => void;
  onToggleExpand: (runId: string) => void;
  /** If true, shows workflow name in run items. If false (default), shows date as primary text */
  showWorkflowName?: boolean;
};

const useStyles = makeStyles({
  container: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalS,
  },
  emptyText: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export const RunsList = ({
  runs,
  isLoading,
  selectedRunId,
  selectedExecutionId,
  expandedRunIds,
  onSelectRun,
  onSelectExecution,
  onToggleExpand,
  showWorkflowName = false,
}: RunsListProps) => {
  const styles = useStyles();

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="small" label="Loading runs..." />
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <Text className={styles.emptyText}>No runs found</Text>
        <Text className={styles.emptyText} size={200}>
          Try adjusting your filters
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.list} role="listbox" aria-label="Workflow runs">
        {runs.map((run) => (
          <RunItem
            key={run.id}
            run={run}
            isSelected={run.id === selectedRunId}
            selectedExecutionId={selectedExecutionId}
            onSelectRun={onSelectRun}
            onSelectExecution={onSelectExecution}
            isExpanded={expandedRunIds.has(run.id)}
            onToggleExpand={onToggleExpand}
            showWorkflowName={showWorkflowName}
          />
        ))}
      </div>
    </div>
  );
};
